function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
        event,
        this.mainDetailsToggle.querySelector("summary")
      )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
            "transitionend",
            addTrapFocus
          );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
        this.closest(".header-wrapper").classList.contains(
          "header-wrapper--border-bottom"
        )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.scrollbarWidth = getScrollbarWidth();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    document.body.style.paddingRight = `${this.scrollbarWidth}px`;
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.style.paddingRight = "0px";
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");
    if (window.innerWidth < 750) {
      this.addEventListener("click", () => {
        button.click();
      });
    }
    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
      this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }
    this.setSlidePosition(this.slideScrollPosition);
    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
    if (this.slider.getAttribute("data-autoplay") === "true") {
      this.play();
    }
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) +
        1 -
        this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}
customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}
if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

// Marquee Component
class MarqueeComponent extends HTMLElement {
  constructor() {
    super();
    this.myTimer = null;
    this.speed = Number(this.getAttribute("data-speed")) * 60;
    this.direction = this.getAttribute("data-direction") || "left";
    this.marqueeItems = null;
    this.containerWidth = 0;
    this.lastTime = null;
    this.accumulator = 0;
    this.pausedTime = 0;
    this.boundStopScrolling = this.stopScrolling.bind(this);
    this.boundResumeScrolling = this.resumeScrolling.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.init();
  }
  init() {
    this.marqueeItems = this.querySelectorAll(".marquee-item");
    this.containerWidth = this.scrollWidth;
    this.addVisibilityListener();
    if (this.shouldAddHoverEvents()) {
      this.addHoverEvents();
    }
    this.startScrolling();
  }
  addVisibilityListener() {
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }
  shouldAddHoverEvents() {
    return this.hasAttribute("data-hover-stop") && window.innerWidth >= 990;
  }
  handleVisibilityChange() {
    if (document.hidden) {
      this.stopScrolling();
    } else {
      this.resumeScrolling();
    }
  }
  addHoverEvents() {
    this.addEventListener("mouseover", this.boundStopScrolling);
    this.addEventListener("mouseout", this.boundResumeScrolling);
  }
  stopScrolling() {
    if (this.isConnected) {
      this.pausedTime = performance.now() - this.lastTime;
      cancelAnimationFrame(this.myTimer);
    }
  }
  resumeScrolling() {
    if (this.isConnected) {
      this.lastTime = performance.now();
      this.accumulator = 0;
      this.pausedTime = 0;
      this.startScrolling();
    }
  }
  startScrolling() {
    cancelAnimationFrame(this.myTimer);
    const scroll = (timestamp) => {
      if (!this.lastTime) this.lastTime = timestamp;
      let deltaTime = timestamp - this.lastTime;
      if (deltaTime > 1000) deltaTime = 1000;
      this.lastTime = timestamp;
      this.moveDiv(deltaTime);
      this.myTimer = requestAnimationFrame(scroll);
    };
    this.myTimer = requestAnimationFrame(scroll);
  }
  moveDiv(deltaTime) {
    const isLeft = this.direction === "left";
    const pixelsPerMillisecond = this.speed / 1000;
    const pixelsToMove = pixelsPerMillisecond * deltaTime + this.accumulator;
    const integerPixels = Math.floor(pixelsToMove);
    this.accumulator = pixelsToMove - integerPixels;
    if (integerPixels > 0) {
      this.scrollLeft += isLeft ? integerPixels : -integerPixels;
    }
    this.containerWidth = this.scrollWidth;
    if (this.shouldResetScroll(isLeft)) {
      this.resetScrollPosition(isLeft);
    }
  }
  shouldResetScroll(isLeft) {
    const threshold = this.containerWidth / 2;
    return (
      (isLeft && this.scrollLeft >= threshold) ||
      (!isLeft && this.scrollLeft <= threshold)
    );
  }
  resetScrollPosition(isLeft) {
    const containerRect = this.getBoundingClientRect();
    const edgePosition = isLeft ? containerRect.left : containerRect.right;
    const tolerance = 1;
    for (const item of this.marqueeItems) {
      const element = isLeft ? item.firstElementChild : item.lastElementChild;
      const rect = element.getBoundingClientRect();
      const elementEdge = isLeft ? rect.left : rect.right;
      if (Math.abs(elementEdge - edgePosition) <= tolerance) {
        this.scrollLeft = isLeft ? 0 : this.scrollWidth;
        break;
      }
    }
    if (!isLeft && this.scrollLeft <= 0) {
      this.scrollLeft = this.scrollWidth;
    }
  }
}
customElements.define("marquee-component", MarqueeComponent);

// Header Component
class HeaderComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const mainContent = document.querySelector("#MainContent");
    if (mainContent) {
      const firstSection = mainContent.querySelector("section.shopify-section");
      if (firstSection) {
        const firstDiv = firstSection.querySelector(
          "[under-transparent-header]"
        );
        if (firstDiv) {
          this.parentElement.setAttribute("transparent-header", "");
        }
      }
    }
  }
}
customElements.define("header-component", HeaderComponent);

// Sticky Header
class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector(".section-header");
    this.headerIsAlwaysSticky =
      this.getAttribute("data-sticky-type") === "always" ||
      this.getAttribute("data-sticky-type") === "reduce-logo-size";
    this.headerBounds = {};
    this.transparentHeader = this.hasAttribute("transparent-header");

    this.setHeaderHeight();

    window
      .matchMedia("(max-width: 990px)")
      .addEventListener("change", this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add("shopify-section-header-sticky");
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector("predictive-search");

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
    window.addEventListener("scroll", this.onScrollHandler, false);

    this.createObserver();
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty(
      "--header-height",
      `${this.header.offsetHeight}px`
    );
  }

  disconnectedCallback() {
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
    this.removeEventListener("mouseover", this.boundStopScrolling);
    this.removeEventListener("mouseout", this.boundResumeScrolling);
    cancelAnimationFrame(this.myTimer);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    // 添加对headerIsAlwaysSticky的特殊处理
    if (this.headerIsAlwaysSticky && this.transparentHeader) {
      if (scrollTop === 0) {
        this.setAttribute("transparent-header", "");
      } else {
        this.removeAttribute("transparent-header");
      }
    }

    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      this.header.classList.add("scrolled-past-header");
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      this.header.classList.add("scrolled-past-header");
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);
        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);
        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove("scrolled-past-header");
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add(
      "shopify-section-header-hidden",
      "shopify-section-header-sticky"
    );
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add("shopify-section-header-sticky", "animate");
    this.header.classList.remove("shopify-section-header-hidden");
    if (this.transparentHeader) {
      this.removeAttribute("transparent-header");
    }
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove(
      "shopify-section-header-hidden",
      "shopify-section-header-sticky",
      "animate"
    );
    if (this.transparentHeader) {
      this.setAttribute("transparent-header", "");
    }
  }

  closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("header-menu");
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal =
      this.searchModal || this.header.querySelector("details-modal");
    this.searchModal.close(false);
  }
}
customElements.define("sticky-header", StickyHeader);

// Count Down Component
class CountDown extends HTMLElement {
  connectedCallback() {
    this.daysEl = this.querySelector(".count-down_days");
    this.hoursEl = this.querySelector(".count-down_hours");
    this.minutesEl = this.querySelector(".count-down_minutes");
    this.secondsEl = this.querySelector(".count-down_seconds");

    this.endTime = new Date(this.dataset.endtime).getTime();
    this.animationFrame = null;
    this.isVisible = true;

    // 处理页面可见性变化
    document.addEventListener("visibilitychange", () => {
      this.isVisible = !document.hidden;
      if (this.isVisible) this.update();
    });

    this.update();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.animationFrame);
  }

  update = () => {
    if (!this.isVisible) return;

    const now = new Date().getTime();
    const diff = this.endTime - now;

    if (diff <= 0) {
      this.setTime(0, 0, 0, 0);
      this.hide();
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    this.setTime(days, hours, minutes, seconds);
    this.animationFrame = requestAnimationFrame(this.update);
  };

  setTime(d, h, m, s) {
    this.daysEl.textContent = d.toString().padStart(2, "0");
    this.hoursEl.textContent = h.toString().padStart(2, "0");
    this.minutesEl.textContent = m.toString().padStart(2, "0");
    this.secondsEl.textContent = s.toString().padStart(2, "0");
  }

  hide() {
    const selector = this.dataset.controlHiddenEl;
    if (!selector) return;
    const targetEl = document.querySelector(selector);
    if (targetEl) {
      targetEl.style.display = "none";
    }
  }
}
customElements.define("count-down", CountDown);

// Swiper Component
document.addEventListener("DOMContentLoaded", () => {
  class SwiperComponent extends HTMLElement {
    constructor() {
      super();
      this.showDesktop = this.getAttribute("show-desktop") !== "false";
      this.showMobile = this.getAttribute("show-mobile") !== "false";
      this.dynamicPagination = JSON.parse(
        this.getAttribute("dynamic-pagination") || "{}"
      );
      this.paginationConfig = JSON.parse(
        this.getAttribute("pagination") || "{}"
      );

      this.init();
      this.observeBodyStyle();
    }

    get _paginationEl() {
      return this.querySelector(".swiper-pagination");
    }

    static hasObserver = false;

    observeBodyStyle() {
      if (SwiperComponent.hasObserver) return;
      SwiperComponent.hasObserver = true;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === "style") {
            const overflowHidden =
              getComputedStyle(document.body).overflow === "hidden";
            document.querySelectorAll("swiper-component").forEach((el) => {
              if (el.swiper) {
                el.swiper.enabled = !overflowHidden;
              }
            });
          }
        });
      });
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }

    init() {
      const isDesktop = window.innerWidth >= 750;
      if (
        (isDesktop && !this.showDesktop) ||
        (!isDesktop && !this.showMobile)
      ) {
        this.style.display = "block";
        return;
      }

      // 获取所有 slides
      const slides = this.querySelectorAll('.swiper-slide');
      const slidesCount = slides.length;


      const loopedSlides = this.getAttribute("loopedSlides") || Math.min(slidesCount, 3);
      const isLoop = this.getAttribute("loop") === "true";

      this.style.visibility = 'hidden';

      const swiper = new Swiper(this, {
        slidesPerView: Number(this.getAttribute("slidesPerView")) || 1,
        spaceBetween: Number(this.getAttribute("spaceBetween")) || 0,
        direction: this.getAttribute("direction") || "horizontal",
        speed: Number(this.getAttribute("speed")) || 600,
        loop: isLoop,
        loopedSlides: isLoop ? Number(loopedSlides) : 0,
        grabCursor: isDesktop,
        centeredSlides: this.getAttribute("centeredSlides") === "true",
        autoplay: JSON.parse(this.getAttribute("autoplay")) || false,
        effect: this.getAttribute("effect") || "slide",
        autoHeight: this.getAttribute("autoHeight") === "true",
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        initialSlide: 0,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: `${this.getAttribute("navigationEl")} .swiper-button-next`,
          prevEl: `${this.getAttribute("navigationEl")} .swiper-button-prev`,
        },
        pagination: {
          ...this._parsePaginationConfig(),
        },
        breakpoints: JSON.parse(this.getAttribute("breakpoints") || "{}"),
        on: {
          init: (swiper) => {
            // 确保在初始化完成后再显示
            setTimeout(() => {
              this.style.display = 'block';
              this.style.visibility = 'visible';
              if (this._paginationEl) this._paginationEl.style.opacity = '1';
              if (this.paginationConfig.scroll) this._setupScrollPagination();

              // 强制更新 slides 状态
              swiper.update();
              if (isLoop) {
                swiper.loopFix();
                // 确保循环模式下的正确显示
                swiper.slideToLoop(0, 0);
              }

              // 添加额外的样式修复
            }, 100);
          },
          autoplayTimeLeft: (s, time, progress) => {
            const activeBullet = this.querySelector(
              ".swiper-pagination-bullet-active svg"
            );
            if (activeBullet) {
              activeBullet.style.setProperty("--progress", `${1 - progress}`);
            }
          },
          slideChange: (swiper) => {
            if (this.paginationConfig.scroll)
              this._handleScrollPaginationOnSlideChange();
            if (this.dynamicPagination)
              this._updateDynamicPaginationProperties();
          },
          // 添加 transitionEnd 事件确保过渡完成后更新状态
          transitionEnd: (swiper) => {
            if (isLoop) {
              swiper.loopFix();
            }
          }
        },
      });

      this.swiper = swiper;

      if (this.getAttribute("hoverchange") === "true") {
        const bullets = this.querySelectorAll(".swiper-pagination-bullet");
        bullets.forEach((bullet, index) => {
          bullet.addEventListener("mouseover", () => swiper.slideTo(index));
        });
      }
    }

    _parsePaginationConfig() {
      const { el, clickable, renderBullet } = this.paginationConfig;
      if (renderBullet == true) {
        const paginationEls = this.querySelectorAll(
          ".swiper-pagination-bullet"
        );
        if (paginationEls) {
          this.paginationConfig.renderBullet = (index, className) => {
            return `<div class="${className}">${paginationEls[index].innerHTML}</div>`;
          };
        }
      }
      return {
        el,
        clickable,
        renderBullet: this.paginationConfig.renderBullet,
      };
    }

    _setupScrollPagination() {
      const scrollPaginationEl = this._paginationEl;
      if (!scrollPaginationEl) return;

      const bullets = scrollPaginationEl.querySelectorAll(
        ".swiper-pagination-bullet"
      );
      const bulletsWidth = Array.from(bullets).reduce(
        (sum, bullet) => sum + bullet.offsetWidth,
        0
      );

      const containerWidth = scrollPaginationEl.offsetWidth;
      if (bulletsWidth > containerWidth) {
        scrollPaginationEl.style.overflowX = "auto";
        scrollPaginationEl.style.justifyContent = "flex-start";
      }
    }

    _handleScrollPaginationOnSlideChange() {
      const paginationEl = this._paginationEl;
      if (!paginationEl) return;

      const activeBullet = paginationEl.querySelector(
        ".swiper-pagination-bullet-active"
      );
      if (!activeBullet) return;

      const { offsetWidth, offsetLeft } = activeBullet;
      const containerWidth = paginationEl.offsetWidth;

      paginationEl.scrollTo({
        left:
          offsetLeft > containerWidth / 2 ? offsetLeft - offsetWidth / 2 : 0,
        behavior: "smooth",
      });
    }

    _updateDynamicPaginationProperties() {
      const activeBullet = this.querySelector(
        ".swiper-pagination-bullet-active"
      );
      if (!activeBullet) return;

      const { widthProperty, leftProperty } = this.dynamicPagination;

      if (widthProperty) {
        document.documentElement.style.setProperty(
          widthProperty,
          `${activeBullet.offsetWidth}px`
        );
      }
      if (leftProperty) {
        document.documentElement.style.setProperty(
          leftProperty,
          `${activeBullet.offsetLeft}px`
        );
      }
    }

    disconnectedCallback() {
      this.swiper?.destroy(true, true);
    }

    slideTo(index) {
      if (this.swiper) {
        this.swiper.slideTo(index);
      }
    }
  }
  customElements.define("swiper-component", SwiperComponent);
});

//collapsible-content
class CollapsibleContent extends HTMLElement {
  constructor() {
    super();
    this.summary = this.querySelector("summary");
    this.content = this.querySelector("content");
    this.animation = null;
  }

  connectedCallback() {
    this.summary.addEventListener("click", this.onClick.bind(this));
    // 初始化状态
    this.content.style.maxHeight = this.open
      ? `${this.content.scrollHeight}px`
      : "0";
  }

  get open() {
    return this.hasAttribute("open");
  }

  onClick(event) {
    event.preventDefault();
    this.toggle();
  }

  toggle() {
    this.open ? this.close() : this.openSection();
  }

  openSection() {
    this.setAttribute("open", "");
    const startHeight = this.content.scrollHeight;
    this.content.style.maxHeight = `${startHeight}px`;
    this.summary.setAttribute("aria-expanded", "true");
  }

  close() {
    const startHeight = this.content.scrollHeight;
    const endHeight = 0;
    this.content.style.maxHeight = `${startHeight}px`;
    requestAnimationFrame(() => {
      this.content.style.maxHeight = `${endHeight}px`;
    });
    this.summary.setAttribute("aria-expanded", "false");
    this.removeAttribute("open");
  }
}

customElements.define("collapsible-content", CollapsibleContent);

// discount code footer
class DiscountCodeFooter extends HTMLElement {
  constructor() {
    super();
    this.discountCodeFooter = this;
    this.clearBtn = this.querySelector("#clear-discount-btn");
    this.applyBtn = this.querySelector("#apply-discount-btn");
    this.discountCodeError = this.querySelector("#discount-code-error");
    this.discountCodeWrapper = this.querySelector(
      "#applied-discount-code .applied-discount-code-wrapper"
    );
    this.discountCodeValue = this.querySelector(
      "#applied-discount-code .applied-discount-code-value"
    );
    this.total_price = this.querySelector("#total_price");
    this.discountCodeInput = this.querySelector("#discount-code-input");
  }

  connectedCallback() {
    if (localStorage.discountCode) {
      this.applyDiscount(JSON.parse(localStorage.discountCode).code);
    }

    if (this.applyBtn) {
      this.applyBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.applyDiscount(this.discountCodeInput.value).then(() => {
          this.discountCodeInput.value = "";
        });
      });
    }

    if (this.clearBtn) {
      this.clearBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.clearDiscount();
      });
    }
  }

  setLoadingState(isLoading) {
    let overlay = this.discountCodeFooter.querySelector(".loading-overlay");

    if (!overlay) {
      overlay = document.createElement("div");
      overlay.className = "loading-overlay";
      overlay.innerHTML = `<div class='loader'></div>`;
      this.discountCodeFooter.appendChild(overlay);
    }
    requestAnimationFrame(() => {
      overlay.classList.toggle("active", isLoading);
    });
    // 按钮状态控制
    if (this.applyBtn) {
      this.applyBtn.innerHTML = isLoading ? "APPLYING" : "APPLY";
      this.applyBtn.style.pointerEvents = isLoading ? "none" : "all";
    }
  }

  async clearDiscount() {
    try {
      this.setLoadingState(true);
      this.discountCodeValue && (this.discountCodeValue.innerHTML = "");
      this.discountCodeError && (this.discountCodeError.innerHTML = "");
      this.clearLocalStorage();
      const response = await fetch("/discount/CLEAR");
      if (!response.ok) throw new Error("Clear discount failed");
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      this.updateTotalPrice(doc);
    } catch (error) {
      console.error("Error clearing discount:", error);
      this.discountCodeError &&
        (this.discountCodeError.innerHTML = error.message);
    } finally {
      this.setLoadingState(false);
    }
  }

  updateTotalPrice(doc) {
    if (!this.total_price) return;
    const newTotal = doc.querySelector("#total_price");
    const freeShippingBar = doc.querySelector(".cart-drawer__free-shipping");
    if (newTotal) {
      this.total_price.innerHTML = newTotal.innerHTML;
    }
    if (freeShippingBar) {
      const cartDrawerFreeShippingBar = document.querySelector(
        ".cart-drawer__free-shipping"
      );
      if (cartDrawerFreeShippingBar) {
        cartDrawerFreeShippingBar.replaceWith(freeShippingBar);
      }
    }
  }

  clearLocalStorage() {
    if (this.discountCodeWrapper) {
      this.discountCodeWrapper.style.display = "none";
    }
    localStorage.removeItem("discountCode");
  }

  async applyDiscount(code) {
    if (!code?.trim()) {
      this.discountCodeError &&
        (this.discountCodeError.innerHTML = "Please enter a discount code");
      return;
    }
    try {
      this.setLoadingState(true);
      const discountResponse = await fetch(`/discount/${code}?redirect=/cart`);
      if (!discountResponse.ok) throw new Error("Invalid discount code");
      const [cartHtml, updatedCart] = await Promise.all([
        discountResponse.text(),
        fetch("/cart.js").then((r) => r.json()),
      ]);
      if (updatedCart.total_discount <= 0)
        throw new Error("Invalid discount code");
      const cartDoc = new DOMParser().parseFromString(cartHtml, "text/html");
      this.handleSuccessfulDiscount(code, updatedCart, cartDoc);
    } catch (error) {
      this.handleDiscountError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  handleSuccessfulDiscount(code, cartData, cartDoc) {
    this.discountCodeWrapper &&
      (this.discountCodeWrapper.style.display = "inline");
    this.discountCodeError && (this.discountCodeError.innerHTML = "");
    if (this.discountCodeValue) {
      const discountAmount = cartData.total_discount / 100;
      this.discountCodeValue.innerHTML = `${code} (${discountAmount} ${Shopify.currency.active})`;
    }
    this.updateTotalPrice(cartDoc);
    this.saveToLocalStorage(code, cartData);
  }

  handleDiscountError(error) {
    this.discountCodeValue && (this.discountCodeValue.innerHTML = "");
    this.clearLocalStorage();
    this.discountCodeError &&
      (this.discountCodeError.innerHTML = error.message);
  }

  saveToLocalStorage(code, cartData) {
    localStorage.setItem(
      "discountCode",
      JSON.stringify({
        code: code.trim(),
        totalCart: (cartData.original_total_price / 100).toFixed(2),
      })
    );
  }
}
customElements.define("discount-code-footer", DiscountCodeFooter);

class TabsElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.buttons.forEach((button, index) =>
      button.addEventListener("click", () => (this.selectedIndex = index))
    );

    if (Shopify.designMode) {
      this.addEventListener(
        "shopify:block:select",
        (event) => (this.selectedIndex = this.buttons.indexOf(event.target))
      );
    }
  }

  static get observedAttributes() {
    return ["selected-index"];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute(
      "selected-index",
      Math.min(Math.max(index, 0), this.buttons.length - 1).toString()
    );
  }

  get buttons() {
    return (this._buttons =
      this._buttons || Array.from(this.querySelectorAll('button[role="tab"]')));
  }

  get indicators() {
    return (this._indicators =
      this._indicators || Array.from(this.querySelectorAll(".indicators")));
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle("button--primary", index === parseInt(newValue));
      button.classList.toggle(
        "button--secondary",
        index !== parseInt(newValue)
      );
      button.disabled = index === parseInt(newValue);
    });

    this.indicators.forEach((button, index) => {
      button.hidden = index !== parseInt(newValue);
    });

    if (
      name === "selected-index" &&
      oldValue !== null &&
      oldValue !== newValue
    ) {
      const fromButton = this.buttons[parseInt(oldValue)];
      const toButton = this.buttons[parseInt(newValue)];
      this.updateSliderVisibility(parseInt(newValue));
    }
  }
  // 新增的函数
  updateSliderVisibility(index) {
    const panels = this.parentNode.querySelectorAll('div[role="tabpanel"]');
    panels.forEach((panel, i) => {
      const panelId = panel.getAttribute("id");
      if (
        panelId ===
        `TabPanel-${this.buttons[index].getAttribute("aria-controls").split("-")[1]
        }`
      ) {
        panel.hidden = false;
      } else {
        panel.hidden = true;
      }
    });
  }
}
customElements.define("tabs-element", TabsElement);
class CopyButton extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async () => {
      try {
        const copyValue = this.dataset.copyValue || this.dataset.copyText;
        await navigator.clipboard.writeText(copyValue);
        const originalText = this.textContent;
        this.textContent = this.dataset.successText;
        setTimeout(() => {
          this.textContent = originalText;
        }, 10000);
      } catch (err) {
        console.error("COPY BUTTON ERROR:", err);
        this.textContent = this.dataset.copyText;
      }
    });
  }
}
customElements.define("copy-button", CopyButton);

function getScrollbarWidth() {
  return window.innerWidth - document.body.clientWidth;
}

// js/sections/before-after-image.js
class SplitCursor extends HTMLElement {
  connectedCallback() {
    this._parentSection = this.closest(".shopify-section");
    this._container = this.closest(".before-after");
    this._dragging = false;
    this._offsetX = this._currentX = 0;

    // Bind events to the cursor wrapper instead of the section
    this._container.addEventListener(
      "pointerdown",
      this._onPointerDown.bind(this)
    );
    this._container.addEventListener(
      "pointermove",
      this._onPointerMove.bind(this)
    );
    this._container.addEventListener("pointerup", this._onPointerUp.bind(this));
    this._container.addEventListener(
      "pointerleave",
      this._onPointerUp.bind(this)
    );

    this._recalculateOffset();
    window.addEventListener("resize", this._recalculateOffset.bind(this));

    // Set initial position
    const initialPosition = parseFloat(
      this.getAttribute("data-drag-position") || 50
    );
    this._setPosition(initialPosition);
  }

  get minOffset() {
    return 0;
  }

  get maxOffset() {
    const containerRect = this._container.getBoundingClientRect();
    return containerRect.width;
  }

  _onPointerDown(event) {
    if (event.target === this || this.contains(event.target)) {
      event.preventDefault();
      this._dragging = true;
      this._container.style.cursor = "grabbing";
      this._initialX = event.clientX - this._offsetX;

      // Capture pointer to ensure smooth dragging
      this.setPointerCapture(event.pointerId);
    }
  }

  _onPointerMove(event) {
    if (!this._dragging) return;

    event.preventDefault();
    const containerRect = this._container.getBoundingClientRect();
    const newX = Math.max(
      0,
      Math.min(containerRect.width, event.clientX - containerRect.left)
    );
    const percentage = (newX / containerRect.width) * 100;

    this._setPosition(percentage);
  }

  _onPointerUp(event) {
    if (this._dragging) {
      this._dragging = false;
      this._container.style.cursor = "";

      // Release pointer capture
      if (event.pointerId !== undefined) {
        this.releasePointerCapture(event.pointerId);
      }
    }
  }

  _setPosition(percentage) {
    const boundedPercentage = Math.max(0, Math.min(100, percentage));
    this._currentX = (boundedPercentage / 100) * this.maxOffset;
    this._offsetX = this._currentX;

    // Update clip path for the after image
    const afterImage = this._container.querySelector(
      ".before-after__after-image"
    );
    if (afterImage) {
      this._container.style.setProperty(
        "--clip-path-offset",
        `${boundedPercentage}%`
      );
    }

    // Update cursor position
    this.style.transform = `translateX(${this._currentX}px) translateX(-50%)`;
  }

  _recalculateOffset() {
    const percentage = (this._currentX / this.maxOffset) * 100;
    this._setPosition(percentage);
  }
}
customElements.define("split-cursor", SplitCursor);

//footer-block
class FooterBlock extends HTMLElement {
  constructor() {
    super();
    this.footerBlockHeading = this.querySelector("h2.footer-block__heading");
    this.footerBlockMenu = this.querySelector("ul.list-unstyled");
    this.footerBlockMenuMaxHeight = Array.from(this.footerBlockMenu.querySelectorAll("li")).reduce((sum, li) => sum + li.offsetHeight, 0);
  }

  connectedCallback() {
    this.footerBlockHeading.addEventListener("click", () => {
      if (window.innerWidth <= 749) {
        const currentMaxHeight = window.getComputedStyle(this.footerBlockMenu).getPropertyValue('max-height');
        this.footerBlockMenu.style.maxHeight =
          currentMaxHeight === "0px"
            ? `${this.footerBlockMenuMaxHeight}px`
            : "0px";
        this.footerBlockHeading.classList.toggle("active");
      }
    });
  }
}
customElements.define("footer-block", FooterBlock);

class ProductCardVariants extends HTMLElement {
  constructor() {
    super();
    this.init();
  }
  
  init() {
    // 绑定变体切换按钮的点击事件
    this.addEventListener('click', this.handleVariantSwitch.bind(this));
  }
  
  handleVariantSwitch(event) {
    const button = event.target.closest('.product-card-variants__color-option');
    if (!button) return;
    
    event.preventDefault();
    
    const variantId = button.dataset.variantId;
    const productId = this.dataset.productId;
    
    // 从当前容器向上查找产品卡片容器
    const cardElement = this.closest('#card-product-' + productId) || 
                       this.closest('.card-wrapper, .product-card-wrapper');

    if (!cardElement) return;

    // 移除所有活动状态
    this.querySelectorAll('.product-card-variants__color-option').forEach((btn) => {
      btn.classList.remove('active');
    });

    // 添加活动状态到当前按钮
    button.classList.add('active');
    
    // 请求产品JSON数据并更新卡片内容
    this.updateProductCard(variantId, productId, cardElement);
  }
  
  updateProductCard(variantId, productId, cardElement) {
    const productHandle = this.getProductHandle();
    if (!productHandle) return;
    
    fetch(`/products/${productHandle}.js`)
      .then(response => response.json())
      .then(product => {
        // 找到对应的变体
        const variant = product.variants.find(v => v.id == variantId);
        if (!variant) return;
        
        // 更新产品卡片的关键信息
        this.updateCardContent(cardElement, variant, product);
      })
      .catch(error => {
        console.error('Error updating product card:', error);
      });
  }
  
  getProductHandle() {
    // 方法1: 从变体URL中提取handle
    const variantUrl = this.querySelector('.product-card-variants__color-option')?.dataset.variantUrl;
    if (variantUrl) {
      const match = variantUrl.match(/\/products\/([^\/\?]+)/);
      if (match) {
        return match[1];
      }
    }
    
    // 方法2: 从产品卡片的链接中提取handle
    const productUrl = this.closest('.card, .product-card')?.querySelector('a[href*="/products/"]')?.href;
    if (productUrl) {
      const match = productUrl.match(/\/products\/([^\/\?]+)/);
      if (match) {
        return match[1];
      }
    }
    
    return null;
  }
  
  updateCardContent(cardElement, variant, product) {
    const variantUrl = `${product.url}?variant=${variant.id}`;
    
    // 更新产品图片
    const productImage = cardElement.querySelector('.card__media img, .product-card__image img');
    if (productImage && variant.featured_image) {
      productImage.src = variant.featured_image.src;
      productImage.srcset = this.generateSrcset(variant.featured_image.src);
      productImage.alt = variant.featured_image.alt || product.title;
    }
    
    // 更新库存状态和徽章
    const badgeElement = cardElement.querySelector('.card__badge');
    if (badgeElement) {
      // 获取现有badge的color-scheme类名
      const existingBadge = badgeElement.querySelector('.badge');
      let colorSchemeClass = '';
      if (existingBadge) {
        const colorMatch = existingBadge.className.match(/color-[\w-]+/);
        colorSchemeClass = colorMatch ? ` ${colorMatch[0]}` : '';
      }
      
      badgeElement.innerHTML = '';
      if (!variant.available) {
        badgeElement.innerHTML = `<span class="badge badge-custom badge--sold-out${colorSchemeClass}">Sold out</span>`;
      } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        badgeElement.innerHTML = `<span class="badge badge-custom badge--sale${colorSchemeClass}">Sale</span>`;
      }
    }

    // 更新quick-add按钮的data-product-url
    const quickAddSubmit = cardElement.querySelector('button[type="submit"].quick-add__submit');
    if (quickAddSubmit) {
      quickAddSubmit.setAttribute('data-product-url', variantUrl);
    }
    
    // 更新所有产品链接中的variant参数
    const productLinks = cardElement.querySelectorAll('a[href*="/products/"]');
    productLinks.forEach(link => {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('variant', variant.id);
      link.href = url.toString();
    });
  }
  
  generateSrcset(imageSrc) {
    const baseUrl = imageSrc.split('?')[0];
    return [
      `${baseUrl}?v=${Date.now()}&width=165 165w`,
      `${baseUrl}?v=${Date.now()}&width=330 330w`,
      `${baseUrl}?v=${Date.now()}&width=535 535w`,
      `${baseUrl}?v=${Date.now()}&width=750 750w`
    ].join(', ');
  }
}
customElements.define("product-card-variants", ProductCardVariants);